type FormatCurrencyOptions = {
  locale?: string; // default: en-IN
  currency?: string; // default: INR
  showSymbol?: boolean; // default: true
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  compact?: boolean; // when true, formats as 2.5K / 1.2L / 3.5Cr
};

const COMPACT_TIERS = [
  { threshold: 1_00_00_000, suffix: "Cr" },
  { threshold: 1_00_000, suffix: "L" },
  { threshold: 1_000, suffix: "K" },
] as const;

export function formatCurrency(
  amount: number | string | null | undefined,
  {
    locale = "en-IN",
    currency = "INR",
    showSymbol = true,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    compact = false,
  }: FormatCurrencyOptions = {},
): string {
  if (amount === null || amount === undefined || amount === "") {
    return "-";
  }

  const value = Number(amount);
  if (Number.isNaN(value)) return "-";

  if (compact) {
    const abs = Math.abs(value);
    const sign = value < 0 ? "-" : "";
    const symbol = showSymbol ? getCurrencySymbol(currency, locale) : "";
    const tier = COMPACT_TIERS.find(({ threshold }) => abs >= threshold);
    if (tier) {
      const divided = abs / tier.threshold;
      const formatted = divided % 1 === 0
        ? divided.toFixed(0)
        : divided.toFixed(1).replace(/\.0$/, "");
      return `${sign}${symbol}${formatted}${tier.suffix}`;
    }
    // Below 1K — fall through to normal formatting
  }

  const formatter = new Intl.NumberFormat(locale, {
    style: showSymbol ? "currency" : "decimal",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return formatter.format(value);
}

export function getCurrencySymbol(
  currency: string = "INR",
  locale: string = "en-IN",
): string {
  const parts = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
  }).formatToParts(0);

  return parts.find((p) => p.type === "currency")?.value ?? currency;
}
